import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Bookmark, 
  X, 
  Plus, 
  Folder,
  ChevronRight,
  Trash2
} from 'lucide-react';

interface SavedList {
  id: string;
  name: string;
  investorIds: string[];
  createdAt: string;
}

interface SavedListsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentInvestorIds?: string[];
  onLoadList?: (investorIds: string[]) => void;
}

export function SavedLists({ open, onOpenChange, currentInvestorIds, onLoadList }: SavedListsProps) {
  const [lists, setLists] = useState<SavedList[]>([]);
  const [newListName, setNewListName] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  // Load from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('backr_saved_lists');
    if (saved) {
      try {
        setLists(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved lists');
      }
    }
  }, []);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('backr_saved_lists', JSON.stringify(lists));
  }, [lists]);

  const createList = () => {
    if (!newListName.trim() || !currentInvestorIds?.length) return;
    
    const newList: SavedList = {
      id: Date.now().toString(),
      name: newListName.trim(),
      investorIds: currentInvestorIds,
      createdAt: new Date().toISOString()
    };
    
    setLists([newList, ...lists]);
    setNewListName('');
    setShowCreate(false);
  };

  const deleteList = (id: string) => {
    setLists(lists.filter(l => l.id !== id));
  };

  const loadList = (investorIds: string[]) => {
    onLoadList?.(investorIds);
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={() => onOpenChange(false)}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-background rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#2B7FFF] to-[#1A5FCC] p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Folder className="w-6 h-6" />
                <h2 className="text-xl font-bold">Saved Lists</h2>
              </div>
              <button
                onClick={() => onOpenChange(false)}
                className="text-white/70 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-white/80 text-sm mt-1">
              Save and organize your investor research
            </p>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {/* Create New */}
            {showCreate ? (
              <div className="mb-6 p-4 bg-muted rounded-2xl">
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  List Name
                </label>
                <Input
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="e.g., SaaS Seed Investors"
                  className="mb-3"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={createList}
                    disabled={!newListName.trim() || !currentInvestorIds?.length}
                    className="bg-[#2B7FFF]"
                  >
                    <Bookmark className="w-4 h-4 mr-1" />
                    Save {currentInvestorIds?.length || 0} investors
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCreate(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                onClick={() => setShowCreate(true)}
                disabled={!currentInvestorIds?.length}
                className="w-full mb-6 bg-[#2B7FFF] hover:bg-[#1A5FCC]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Save Current Results
              </Button>
            )}

            {/* Lists */}
            {lists.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Folder className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No saved lists yet</p>
                <p className="text-sm">Save your searches for quick access</p>
              </div>
            ) : (
              <div className="space-y-2">
                {lists.map((list) => (
                  <div
                    key={list.id}
                    className="flex items-center justify-between p-4 bg-muted rounded-2xl hover:bg-slate-100 transition-colors group"
                  >
                    <button
                      onClick={() => loadList(list.investorIds)}
                      className="flex-1 text-left"
                    >
                      <div className="font-medium text-slate-900">{list.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {list.investorIds.length} investors • {new Date(list.createdAt).toLocaleDateString()}
                      </div>
                    </button>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => loadList(list.investorIds)}
                        className="p-2 text-[#2B7FFF] hover:bg-[#2B7FFF]/10 rounded-full"
                        title="Load list"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteList(list.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                        title="Delete list"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

